import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { previewQCMsPdf, triggerDownloadQCMs } from '../lib/api';
import { toast } from 'sonner';
import { Download, Loader2, Users, FlaskConical, FileText, X } from 'lucide-react';

const readBlobError = async (e) => {
  if (e.response?.data instanceof Blob) {
    try { return JSON.parse(await e.response.data.text()).detail; } catch { return null; }
  }
  return e.response?.data?.detail || e.message;
};

export const QcmPreviewDialog = ({ quizId, quizTitle, open, onClose, onNoStudents }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ students: null, mode: null });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || !quizId) return;
    let objectUrl = null;
    let cancelled = false;
    setLoading(true);
    setUrl(null);
    previewQCMsPdf(quizId)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setUrl(objectUrl);
        setMeta({
          students: Number(res.headers['x-students-count']) || null,
          mode: res.headers['x-render-mode'] || null,
        });
      })
      .catch(async (e) => {
        if (cancelled) return;
        const msg = await readBlobError(e);
        onClose();
        if (msg?.toLowerCase().includes('élève') && onNoStudents) onNoStudents();
        else toast.error(msg || 'Erreur de prévisualisation');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open, quizId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await triggerDownloadQCMs(quizId);
      toast.success(`Téléchargement lancé${meta.students ? ` — ${meta.students} copies` : ''}`);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Erreur de téléchargement');
    }
    setGenerating(false);
  };

  const isScience = meta.mode === 'science';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !generating) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 bg-card border-border overflow-hidden flex flex-col [&>button]:hidden" data-testid="qcm-preview-dialog">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-card shrink-0">
          <div className="min-w-0">
            <DialogTitle className="text-sm font-semibold truncate" data-testid="qcm-preview-title">
              Aperçu de la 1ère copie{quizTitle ? ` — ${quizTitle}` : ''}
            </DialogTitle>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              {meta.students !== null && (
                <span className="inline-flex items-center gap-1" data-testid="qcm-preview-students">
                  <Users className="w-3.5 h-3.5" /> {meta.students} élève{meta.students > 1 ? 's' : ''}
                </span>
              )}
              {meta.mode && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${isScience ? 'bg-violet-500/15 text-violet-300' : 'bg-primary/10 text-primary'}`} data-testid="qcm-preview-mode">
                  {isScience ? <FlaskConical className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                  {isScience ? 'Rendu scientifique' : 'Rendu classique'}
                </span>
              )}
              <span className="hidden sm:inline">· Chaque élève recevra une copie mélangée différente</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onClose} disabled={generating} data-testid="qcm-preview-close-btn">
              <X className="w-4 h-4" /><span className="hidden sm:inline ml-1.5">Fermer</span>
            </Button>
            <Button size="sm" onClick={handleGenerate} disabled={generating || loading || !url} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="qcm-preview-generate-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="ml-1.5">{generating ? 'Génération...' : `Générer ${meta.students ? `les ${meta.students} copies` : 'toute la classe'}`}</span>
            </Button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-white relative">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground bg-card" data-testid="qcm-preview-loading">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              <p className="text-sm">Génération de l'aperçu...</p>
            </div>
          )}
          {url && <iframe src={url} title="Aperçu QCM" className="w-full h-full border-0" data-testid="qcm-preview-iframe" />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
